/**
 * Song sections, derived from the lyric sheet.
 *
 * The workspace timeline wants section markers — Verse, Chorus, Bridge — the way a
 * DAW shows arrangement markers. The app has no timing data at all: a Song carries
 * a length string and a lyric sheet, and nothing anywhere records when a chorus
 * starts.
 *
 * So rather than fabricate timestamps, this reads the headings the lyric sheet
 * already contains ("[Verse 1]", "[Chorus]") and places each one at its position in
 * the *text*. A section a third of the way down the page is drawn a third of the way
 * along the track.
 *
 * That is an approximation, and the UI says so — Timeline labels the strip
 * "Approximate song sections". It is a real reading of real data rather than a
 * plausible-looking invention, which is the distinction that matters: when the
 * backend returns actual section boundaries, this function's return type is already
 * the right shape and only its body changes.
 *
 * ============================================================================
 * BATCH 6 §10 — "LYRICS FOLLOW THE SONG", and the line this module will not cross.
 * ============================================================================
 * §10 asks for the lyric sheet to follow playback, and says twice what it must not do:
 * "Do not fake synchronization" and "Do not pretend that exact word/line
 * synchronization exists."
 *
 * There is nothing to synchronise TO. `Song.audioUrl` is null throughout, playback is a
 * 1Hz interval advancing a counter (see hooks/usePlayer.useSimulatedPlayback), and no
 * record in the app carries a timestamp for a lyric — not per word, not per line, not
 * per section. A karaoke highlight would therefore be an animation invented here and
 * presented as a measurement, which is exactly the failure the whole file was written
 * to avoid.
 *
 * What CAN be read honestly is the reading `songSections` already makes, applied at the
 * granularity it actually supports: a section's share of the sheet against the song's
 * share of its length. So `lyricBlocks` groups the sheet into its own sections and
 * `currentBlockIndex` answers "which section is approximately here" — SECTION, never a
 * line and never a word. The UI states it in those words on screen; see
 * components/songs/LyricSheet.
 */
import type { Song } from '../types/domain';

export interface SongSection {
  /** Heading text with the brackets stripped, e.g. "Chorus". */
  name: string;
  /** Position along the track, 0–1. */
  at: number;
}

/** Matches a section heading on its own line: Verse 1 / [Verse 1], Chorus / [Chorus]. */
const HEADING = /^\s*(?:\[([^\]]+)\]|(Intro|Outro|Verse(?:\s+\d+)?|Chorus(?:\s+\d+)?|Bridge|Pre-?Chorus|Hook|Refrain|Refrain\s*\/\s*Outro Hook|Reprise|Section\s+[A-Z0-9]+))\s*$/i;

/**
 * Reads section headings out of a song's lyrics.
 *
 * Returns an empty array when the lyrics have no headings (or no lyrics), which is
 * the case the caller must handle by drawing no marker strip at all — an empty
 * strip is worse than no strip.
 */
export function songSections(song: Song): SongSection[] {
  /*
    Derived from `lyricBlocks` rather than scanning the sheet a second time, so the
    timeline's markers and the lyric sheet's highlight cannot disagree about where a
    section starts. The numbers are unchanged: a block's `from` IS the heading's share
    of the sheet, which is what this function has always returned.
  */
  return lyricBlocks(song.lyrics)
    .filter((block): block is LyricBlock & { name: string } => block.name !== null)
    .map((block) => ({ name: block.name, at: block.from }));
}

// ---------------------------------------------------------------------------
// §10 — the sheet as blocks, and which block is approximately current
// ---------------------------------------------------------------------------

export interface LyricLine {
  /** The line's text, verbatim. Never a heading — a heading becomes its block's `name`. */
  text: string;
  /**
   * The line's index in the sheet, which is what a renderer should key on.
   *
   * Kept because the blocks are a *view* of the sheet rather than a replacement for it:
   * the index is stable across a re-group, and it is the only stable identity a lyric
   * line has (two verses can legitimately contain the same words).
   */
  line: number;
}

export interface LyricBlock {
  /**
   * The section heading, or null for the lines before the first one.
   *
   * Null rather than an invented name like "Intro": a sheet that opens without a
   * heading has an unnamed opening, and naming it would be the same class of
   * fabrication as inventing a timestamp.
   */
  name: string | null;
  /** The block's lines, with headings removed and blank lines kept as spacing. */
  lines: LyricLine[];
  /** Where the block begins, as a share of the sheet's lines (0–1). */
  from: number;
  /** Where the block ends, as a share of the sheet's lines (0–1). Exclusive. */
  to: number;
}

/**
 * Groups a lyric sheet into its own sections.
 *
 * Takes the text rather than a Song because the Studio's editor holds an unsaved draft
 * and renders the sheet from it, so the caller with the newest text is not always the
 * one holding the record.
 *
 * `from`/`to` are shares of the LINE COUNT, not seconds — the same reading
 * `songSections` has always made, and the reason every label describing them on screen
 * says "approximate". The blocks tile [0, 1) with no gaps, which is what lets
 * `currentBlockIndex` answer without a fallback case.
 */
export function lyricBlocks(lyrics: string): LyricBlock[] {
  const lines = (lyrics || '').split('\n');
  const total = lines.length;
  if (total === 0) return [];

  const blocks: LyricBlock[] = [];
  // The opening block, which exists whether or not the sheet starts with a heading:
  // its `lines` stay empty when it does, and it is dropped below.
  let current: LyricBlock = { name: null, lines: [], from: 0, to: 1 };

  lines.forEach((line, i) => {
    const match = HEADING.exec(line);
    if (match) {
      // A heading closes the block before it and opens the next one AT ITS OWN LINE, so
      // the heading's own position is the boundary rather than the first lyric under it.
      const heading = (match[1] ?? match[2] ?? '').trim();
      current.to = i / total;
      blocks.push(current);
      current = { name: heading, lines: [], from: i / total, to: 1 };
      return;
    }
    current.lines.push({ text: line, line: i });
  });
  blocks.push(current);

  /*
    An empty unnamed opening is dropped — a sheet beginning with `[Verse 1]` has no
    lines before it, and keeping a zero-length block would make `blocks[0]` a section
    nothing can ever be inside. An unnamed opening WITH lines is kept, because those
    lines are lyrics and a reader has to see them.

    Only the first block can be in that state: every other block starts with a heading
    and therefore has a name, even if the heading is followed by nothing.
  */
  return blocks.filter((block, i) => i > 0 || block.name !== null || block.lines.length > 0);
}

/**
 * Which block a playback position falls in, or -1 when nothing can honestly be said.
 *
 * `at` is a FRACTION of the track, 0–1 — `elapsed / durationSec` at the call site. A
 * fraction rather than seconds because that is all the comparison can be: the blocks
 * are positioned by their share of the text, so the only thing they can be matched
 * against is the song's share of its length. Passing seconds would imply this module
 * knew what a second means to a lyric, and it does not.
 *
 * RETURNS -1 IN THREE CASES, and each is a case where a highlight would be a lie:
 *
 *   - no blocks at all: there is no sheet.
 *   - ONE block: the sheet has no internal structure to move through, so "the current
 *     section" would be the whole sheet. Highlighting everything says "we are following
 *     along" while conveying nothing, which is worse than not offering it.
 *   - a non-finite or negative `at`: a song with no parsable length divides by zero.
 *
 * So a caller's rule is one branch — `index < 0` means highlight nothing — and the
 * degenerate cases cannot reach the screen. See components/songs/LyricSheet.
 */
export function currentBlockIndex(blocks: LyricBlock[], at: number): number {
  if (blocks.length < 2) return -1;
  if (!Number.isFinite(at) || at < 0) return -1;

  // Clamped rather than rejected past the end: the clock can sit on the final second
  // while the interval decides whether the track has finished, and the last section is
  // the honest answer for that moment.
  const pos = Math.min(at, 0.999999);
  const found = blocks.findIndex((block) => pos >= block.from && pos < block.to);
  return found;
}
